import imageCompression from 'browser-image-compression';

/**
 * Image-only version of bodyprint-web `utils/media.ts`: the portal uploads logos and banners, so
 * the ffmpeg video path is left out. Images are compressed under the API's 2 MB image limit.
 */
const IMAGE_MAX_SIZE_MB = 1.8;
const IMAGE_MAX_WIDTH_OR_HEIGHT = 1600;

const extensionForMimeType: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

const replaceFileExtension = (fileName: string, extension: string) => {
  const baseName = fileName.replace(/\.[^.]+$/, '') || 'upload';
  return `${baseName}.${extension}`;
};

export const isImageFile = (file: File) => file.type.startsWith('image/');

export async function compressImageFile(
  file: File,
  {
    maxSizeMB = IMAGE_MAX_SIZE_MB,
    maxWidthOrHeight = IMAGE_MAX_WIDTH_OR_HEIGHT,
    initialQuality = 0.84,
  }: {
    maxSizeMB?: number;
    maxWidthOrHeight?: number;
    initialQuality?: number;
  } = {},
): Promise<File> {
  if (!isImageFile(file) || file.type === 'image/svg+xml' || file.type === 'image/gif') return file;

  const compressed = await imageCompression(file, {
    maxSizeMB,
    maxWidthOrHeight,
    initialQuality,
    useWebWorker: true,
    preserveExif: false,
  });

  return compressed.size < file.size ? compressed : file;
}

export async function prepareUploadFile(file: File): Promise<File> {
  return isImageFile(file) ? compressImageFile(file) : file;
}

export function getPreparedFileName(file: File) {
  const extension = extensionForMimeType[file.type];
  if (!extension) return file.name;
  return replaceFileExtension(file.name, extension);
}
